import React, { useState } from 'react';
import { ShieldCheck, Zap, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = `
Política de Privacidad

Última actualización: abril 2026

En Metabolismo 50+, respetamos y protegemos la privacidad de nuestros usuarios. Esta política describe cómo recopilamos, usamos y protegemos tu información personal.

Información que recopilamos:
• Nombre y dirección de correo electrónico al momento de la compra.
• Información de pago procesada de forma segura a través de plataformas de terceros. No almacenamos datos de tarjetas de crédito.

Uso de la información:
• Para procesar tu compra y entregar el producto digital.
• Para enviarte actualizaciones relacionadas con tu compra.
• No vendemos, alquilamos ni compartimos tu información personal con terceros con fines de marketing.

Seguridad:
• Utilizamos medidas de seguridad estándar de la industria para proteger tu información personal.
• Toda la información se transmite mediante conexiones cifradas (SSL).

Tus derechos:
• Puedes solicitar la eliminación de tus datos personales en cualquier momento.
• Puedes optar por no recibir comunicaciones de marketing.

Al realizar tu compra, aceptas los términos de esta política de privacidad.
`.trim();
const termsContent = `
Términos y Condiciones

Última actualización: abril 2026

Al adquirir Metabolismo 50+: El Reinicio de 7 Días, aceptas los siguientes términos y condiciones:

Producto Digital:
• Este es un producto digital de información. Una vez completada la compra, recibirás acceso inmediato al contenido.
• El contenido es para uso personal y no puede ser redistribuido, copiado ni revendido.

Descargo de Responsabilidad Médica:
• Este producto es únicamente informativo y educativo.
• No sustituye el consejo, diagnóstico o tratamiento médico profesional.
• Consulta siempre a tu médico antes de comenzar cualquier programa de salud o cambio en tu alimentación.
• Los resultados pueden variar dependiendo de cada individuo.

Política de Reembolso:
• Debido a la naturaleza digital del producto, todas las ventas son finales.
• Si tienes algún problema con tu compra, contáctanos y haremos lo posible por ayudarte.

Propiedad Intelectual:
• Todo el contenido, diseño y materiales son propiedad de Metabolismo 50+.
• Queda prohibida la reproducción total o parcial sin autorización previa.

Modificaciones:
• Nos reservamos el derecho de modificar estos términos en cualquier momento.
• Los cambios serán efectivos inmediatamente después de su publicación.

Al completar tu compra, confirmas que has leído y aceptas estos términos y condiciones.
`.trim();
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-brand-green text-brand-cream/80 py-12 px-6 text-center" data-id="element-31">
        <div className="max-w-4xl mx-auto" data-id="element-32">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-8" data-id="element-33">
            <div className="flex items-center gap-2" data-id="element-34">
              <ShieldCheck className="w-5 h-5 text-brand-gold" data-id="element-35" />
              <span className="font-medium" data-id="element-36">Compra segura</span>
            </div>
            <div className="hidden md:block w-1 h-1 bg-brand-rose rounded-full" data-id="element-37"></div>
            <div className="flex items-center gap-2" data-id="element-38">
              <Zap className="w-5 h-5 text-brand-gold" data-id="element-39" />
              <span className="font-medium" data-id="element-40">Entrega digital inmediata</span>
            </div>
          </div>

          <p className="text-sm opacity-60 max-w-2xl mx-auto leading-relaxed" data-id="element-41">
            Este producto no sustituye el consejo médico profesional. Los
            resultados pueden variar dependiendo de cada individuo. Consulta a
            tu médico antes de comenzar cualquier programa de salud.
          </p>

          <div className="mt-8 flex items-center justify-center gap-4 text-sm" data-id="element-42">
            <button onClick={() => setActiveModal('privacy')} className="underline underline-offset-2 opacity-70 hover:opacity-100 transition-opacity" data-id="element-43">
              Política de Privacidad
            </button>
            <span className="opacity-40" data-id="element-44">|</span>
            <button onClick={() => setActiveModal('terms')} className="underline underline-offset-2 opacity-70 hover:opacity-100 transition-opacity" data-id="element-45">
              Términos y Condiciones
            </button>
          </div>

          <div className="mt-6 text-xs opacity-50" data-id="element-46">
            &copy; {new Date().getFullYear()} Metabolismo 50+. Todos los
            derechos reservados.
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-47">
        {activeModal && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-48">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60" onClick={() => setActiveModal(null)} data-id="element-49" />

            {/* Modal Content */}
            <motion.div className="relative bg-brand-cream rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} data-id="element-50">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-brand-rose/20" data-id="element-51">
                <h3 className="text-xl font-bold text-brand-green font-heading" data-id="element-52">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-brand-green/10 transition-colors" data-id="element-53">
                  <XIcon className="w-5 h-5 text-brand-green" data-id="element-54" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 overflow-y-auto" data-id="element-55">
                <pre className="whitespace-pre-wrap font-body text-gray-700 text-sm leading-relaxed" data-id="element-56">
                  {activeModal === 'privacy' ? privacyContent : termsContent}
                </pre>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-brand-rose/20 text-center" data-id="element-57">
                <button onClick={() => setActiveModal(null)} className="bg-brand-green text-white font-bold py-2.5 px-8 rounded-full text-sm hover:bg-brand-green/90 transition-colors" data-id="element-58">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}